import {
  saveShapeOf,
  type CallerCheck, type Destination, type Extension, type FlowChange, type FlowMessage, type FlowPieceKind, type HoursCheck, type Lookup,
  type InboundRoute, type IvrMenu, type Queue, type RingGroup, type Schedule,
} from '@arcnet/contracts';

/**
 * The call flow editor's model (docs/41): pure, so the canvas is a function of the live pieces and
 * the draft over them.
 *
 * A piece is shown in its READ shape (what its list endpoint returns, what its Classic editor takes)
 * and saved in its SAVE shape (what its upsert takes, what a draft holds). Converting both ways is
 * what lets the canvas change one exit of a live piece without opening its editor, and lets a
 * draft's changes open in the same editors as the live pieces.
 */

export type Body = Record<string, unknown>;
export type AnyRead = InboundRoute | IvrMenu | RingGroup | Queue | FlowMessage | HoursCheck | CallerCheck | Lookup;

export interface Live {
  routes: InboundRoute[]; ivrs: IvrMenu[]; groups: RingGroup[]; queues: Queue[];
  messages: FlowMessage[]; hours: HoursCheck[];
  /** Caller checks and look ups (docs/41 §16). */
  callers: CallerCheck[]; lookups: Lookup[];
  extensions: Extension[];
  boxes: Array<{ id: string; extension_number: string; extension_name: string }>;
  schedules: Schedule[];
  numbers: Array<{ id: string; e164: string; label: string | null }>;
  media: Array<{ id: string; name: string; duration_sec: number | null }>;
  businessId: string | null;
}

export const KIND_LABEL: Record<FlowPieceKind, string> = {
  inbound_route: 'Call comes in', hours: 'Hours check', ivr: 'IVR menu', ring_group: 'Hunt list', queue: 'Queue', message: 'Message',
  caller: 'Caller check', lookup: 'Look up',
};
export const KIND_GROUP: Record<FlowPieceKind, 'start' | 'decide' | 'people' | 'audio'> = {
  inbound_route: 'start', hours: 'decide', ivr: 'decide', ring_group: 'people', queue: 'people', message: 'audio',
  caller: 'decide', lookup: 'decide',
};
/** The destination type that names each kind, where a step can send a call to it. */
export const DEST_TYPE: Partial<Record<FlowPieceKind, 'ivr' | 'ring_group' | 'queue' | 'message' | 'hours' | 'caller' | 'lookup'>> = {
  ivr: 'ivr', ring_group: 'ring_group', queue: 'queue', message: 'message', hours: 'hours', caller: 'caller', lookup: 'lookup',
};
export const KIND_OF_DEST: Record<string, FlowPieceKind> = {
  ivr: 'ivr', ring_group: 'ring_group', queue: 'queue', message: 'message', hours: 'hours', caller: 'caller', lookup: 'lookup',
};

const dest = (x: unknown): Destination | null => (x && typeof x === 'object' ? x as Destination : null);

// ------------------------------------------------------------------- read -> save
/** Shared with the server, which snapshots live routing the same way (docs/41 §12). */
export const toBody = (kind: FlowPieceKind, r: AnyRead): Body => saveShapeOf(kind, r);

// ------------------------------------------------------------------- save -> read
export function toRead(kind: FlowPieceKind, id: string, b: Body, live: Live): AnyRead {
  const ext = new Map(live.extensions.map((e) => [e.id, e]));
  const str = (x: unknown): string | null => (typeof x === 'string' && x !== '' ? x : null);
  switch (kind) {
    case 'inbound_route': {
      const h = (b.hours ?? { mode: 'always' }) as { mode: string; schedule_id?: string; timezone?: string | null; rules?: Body[] };
      const sched = live.schedules.find((s) => s.id === h.schedule_id);
      const ids = (b.number_ids as string[] | undefined) ?? [];
      return {
        id, name: String(b.name ?? ''), created_at: '', is_enabled: b.is_enabled !== false, cid_name_prefix: str(b.cid_name_prefix),
        numbers: ids.flatMap((n) => { const x = live.numbers.find((m) => m.id === n); return x ? [x] : []; }),
        destination: dest(b.destination) as Destination, closed_destination: dest(b.closed_destination),
        hours: {
          mode: h.mode as 'always' | 'schedule' | 'own',
          schedule_id: h.mode === 'schedule' ? h.schedule_id ?? null : null,
          schedule_name: h.mode === 'schedule' ? sched?.name ?? null : null,
          timezone: h.timezone ?? '',
          rules: h.mode === 'own' ? (h.rules ?? []).map((q, i) => ({
            id: `${id}:${i}`, kind: (q.kind ?? 'weekly') as 'weekly', days_of_week: (q.days_of_week as number[]) ?? null,
            time_from: str(q.time_from), time_to: str(q.time_to), date_from: str(q.date_from), date_to: str(q.date_to),
            is_open: q.is_open !== false, priority: Number(q.priority ?? 0), label: str(q.label),
          })) : [],
          open_now: null, explanation: null,
        },
      } as InboundRoute;
    }
    case 'ivr': {
      const was = live.ivrs.find((m) => m.id === id);
      return {
        id, tenant_id: '', name: String(b.name ?? ''), extension: str(b.extension),
        greeting_media_id: str(b.greeting_media_id), invalid_media_id: str(b.invalid_media_id),
        timeout_sec: Number(b.timeout_sec ?? 5), digit_timeout_sec: Number(b.digit_timeout_sec ?? 3),
        max_attempts: Number(b.max_attempts ?? 3), answer_after_rings: Number(b.answer_after_rings ?? 0),
        direct_dial: b.direct_dial === true, timeout_destination: dest(b.timeout_destination), invalid_destination: dest(b.invalid_destination),
        options: ((b.options as Body[] | undefined) ?? []).map((o) => ({
          id: `${id}:${String(o.digit)}`, digit: String(o.digit), label: str(o.label), destination: dest(o.destination) as Destination,
        })),
        created_at: '',
        ...(was && was.greeting_media_id === str(b.greeting_media_id) ? { greeting_media_name: (was as { greeting_media_name?: string }).greeting_media_name } : {}),
      } as IvrMenu;
    }
    case 'ring_group': {
      const steps = (b.steps as Body[] | undefined) ?? [];
      return {
        id, tenant_id: '', name: String(b.name ?? ''), extension: str(b.extension),
        strategy: (steps[0]?.strategy ?? b.strategy ?? 'simultaneous') as RingGroup['strategy'],
        ring_time_sec: Number(steps[0]?.ring_sec ?? b.ring_time_sec ?? 20), skip_busy: b.skip_busy !== false,
        timeout_destination: dest(b.timeout_destination), outside_extension_id: str(b.outside_extension_id), created_at: '',
        steps: steps.map((s, i) => ({
          seq: i + 1, strategy: s.strategy as RingGroup['strategy'], ring_sec: Number(s.ring_sec ?? 20),
          prompt_media_id: str(s.prompt_media_id), prompt_name: null, members: [],
        })),
        members: steps.flatMap((s, i) => ((s.members as Body[] | undefined) ?? []).map((m, j) => {
          const e = typeof m.extension_id === 'string' ? ext.get(m.extension_id) : undefined;
          return {
            id: `${id}:${i}:${j}`, extension_id: str(m.extension_id), extension_number: e?.number ?? null,
            extension_name: e?.display_name ?? null, external_number: str(m.external_number), position: j, delay_sec: 0, step: i + 1,
          };
        })),
      } as RingGroup;
    }
    case 'queue': {
      return {
        id, tenant_id: '', name: String(b.name ?? ''), extension: str(b.extension),
        strategy: (b.strategy ?? 'longest_idle') as Queue['strategy'], announce_position: b.announce_position === true,
        announce_interval_sec: Number(b.announce_interval_sec ?? 60), periodic_media_id: str(b.periodic_media_id),
        periodic_interval_sec: Number(b.periodic_interval_sec ?? 60), exit_key: str(b.exit_key), exit_destination: dest(b.exit_destination),
        max_wait_sec: Number(b.max_wait_sec ?? 600), max_waiting_calls: Number(b.max_waiting_calls ?? 50),
        wrapup_sec: Number(b.wrapup_sec ?? 10), require_disposition: b.require_disposition === true,
        workspace_controls: (b.workspace_controls ?? {}) as Queue['workspace_controls'],
        sticky_agent_days: (b.sticky_agent_days ?? null) as number | null, sticky_ring_sec: Number(b.sticky_ring_sec ?? 15),
        timeout_destination: dest(b.timeout_destination), moh_media_id: str(b.moh_media_id), created_at: '',
        agents: ((b.agents as Body[] | undefined) ?? []).map((a) => {
          const e = ext.get(String(a.extension_id));
          return {
            id: `${id}:${String(a.extension_id)}`, extension_id: String(a.extension_id),
            extension_number: e?.number ?? '', extension_name: e?.display_name ?? '',
            tier_level: Number(a.tier_level ?? 1), tier_position: Number(a.tier_position ?? 1),
          };
        }),
      } as Queue;
    }
    case 'message': {
      const m = live.media.find((x) => x.id === b.media_id);
      return {
        id, tenant_id: '', name: String(b.name ?? ''), media_id: str(b.media_id), media_name: m?.name ?? null,
        next_destination: dest(b.next_destination), created_at: '',
      };
    }
    case 'hours': {
      const h = (b.hours ?? { mode: 'schedule' }) as { mode: string; schedule_id?: string; timezone?: string | null; rules?: Body[] };
      const sched = live.schedules.find((s) => s.id === h.schedule_id);
      return {
        id, tenant_id: '', name: String(b.name ?? ''), created_at: '',
        open_destination: dest(b.open_destination), closed_destination: dest(b.closed_destination),
        hours: {
          mode: h.mode === 'own' ? 'own' : 'schedule',
          schedule_id: h.mode === 'own' ? null : str(h.schedule_id),
          schedule_name: h.mode === 'own' ? null : sched?.name ?? null,
          timezone: h.timezone ?? '',
          rules: h.mode === 'own' ? (h.rules ?? []).map((q, i) => ({
            id: `${id}:${i}`, kind: (q.kind ?? 'weekly') as 'weekly', days_of_week: (q.days_of_week as number[]) ?? null,
            time_from: str(q.time_from), time_to: str(q.time_to), date_from: str(q.date_from), date_to: str(q.date_to),
            is_open: q.is_open !== false, priority: Number(q.priority ?? 0), label: str(q.label),
          })) : [],
          open_now: null, explanation: null,
        },
      } as HoursCheck;
    }
    case 'caller':
      return {
        id, tenant_id: '', name: String(b.name ?? ''), created_at: '',
        numbers: ((b.numbers as string[] | undefined) ?? []).map(String), match_withheld: b.match_withheld === true,
        match_destination: dest(b.match_destination), no_match_destination: dest(b.no_match_destination),
      };
    case 'lookup': {
      const was = live.lookups.find((x) => x.id === id);
      return {
        id, tenant_id: '', name: String(b.name ?? ''), created_at: '',
        url: String(b.url ?? ''), method: b.method === 'POST' ? 'POST' : 'GET', header_name: str(b.header_name),
        // A value typed into the draft, or one already stored and not removed.
        has_header_value: typeof b.header_value === 'string' ? b.header_value !== '' : b.header_value === null ? false : was?.has_header_value ?? false,
        field: String(b.field ?? ''),
        cases: ((b.cases as Body[] | undefined) ?? []).map((c) => ({ value: String(c.value ?? ''), destination: dest(c.destination) })),
        no_match_destination: dest(b.no_match_destination), error_destination: dest(b.error_destination),
      };
    }
  }
}

/**
 * The account's routing with some pieces as a version had them (docs/41 §12): each of those replaces
 * its live self, or joins the list when it has since been deleted. Everything else stays live, so
 * names of extensions, mailboxes and recordings still resolve.
 */
export function liveAsOf(live: Live, pieces: ReadonlyArray<{ kind: FlowPieceKind; id: string; body: Body }>): Live {
  const swap = <T extends { id: string }>(list: T[], kind: FlowPieceKind): T[] => {
    const mine = pieces.filter((p) => p.kind === kind);
    const ids = new Set(mine.map((p) => p.id));
    return [...list.filter((x) => !ids.has(x.id)), ...mine.map((p) => toRead(kind, p.id, p.body, live) as unknown as T)];
  };
  return {
    ...live,
    routes: swap(live.routes, 'inbound_route'), ivrs: swap(live.ivrs, 'ivr'), groups: swap(live.groups, 'ring_group'),
    queues: swap(live.queues, 'queue'), messages: swap(live.messages, 'message'), hours: swap(live.hours, 'hours'),
    callers: swap(live.callers, 'caller'), lookups: swap(live.lookups, 'lookup'),
  };
}

// ------------------------------------------------------------------- the draft over the live pieces
export type Changes = ReadonlyMap<string, FlowChange>;
export const changeKey = (kind: FlowPieceKind, id: string) => `${kind}:${id}`;

const liveList = (live: Live, kind: FlowPieceKind): AnyRead[] => (
  kind === 'inbound_route' ? live.routes : kind === 'ivr' ? live.ivrs : kind === 'ring_group' ? live.groups
    : kind === 'queue' ? live.queues : kind === 'message' ? live.messages : kind === 'caller' ? live.callers
      : kind === 'lookup' ? live.lookups : live.hours);

/** A piece as the draft has it, else as it is live; null when it is neither. */
export function pieceOf(kind: FlowPieceKind, id: string, live: Live, changes: Changes): AnyRead | null {
  const c = changes.get(changeKey(kind, id));
  if (c) return toRead(kind, id, c.body, live);
  return liveList(live, kind).find((x) => x.id === id) ?? null;
}

/** Its save shape, as the draft has it, else built from the live piece. */
export function bodyOf(kind: FlowPieceKind, id: string, live: Live, changes: Changes): Body | null {
  const c = changes.get(changeKey(kind, id));
  if (c) return c.body;
  const r = liveList(live, kind).find((x) => x.id === id);
  return r ? toBody(kind, r) : null;
}

/** Every piece of a kind, the draft's new ones and renames included, for "Or use one you have". */
export function piecesOf(kind: FlowPieceKind, live: Live, changes: Changes): Array<{ id: string; name: string }> {
  const out = new Map(liveList(live, kind).map((x) => [x.id, x.name]));
  for (const c of changes.values()) if (c.kind === kind) out.set(c.id, String(c.body.name ?? ''));
  return [...out].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

// ------------------------------------------------------------------- exits
/** One way out of a step: where it is in the save shape, what the line into it says. */
export interface Exit { slot: string; label: string | null }

/** The exits of a piece, in the order they are drawn. */
export function exitsOf(kind: FlowPieceKind, b: Body): Exit[] {
  switch (kind) {
    case 'inbound_route': {
      const mode = (b.hours as { mode?: string } | undefined)?.mode ?? 'always';
      return mode === 'always' ? [{ slot: 'destination', label: null }]
        : [{ slot: 'destination', label: 'Open' }, { slot: 'closed_destination', label: 'Closed' }];
    }
    case 'hours': return [{ slot: 'open_destination', label: 'Open' }, { slot: 'closed_destination', label: 'Closed' }];
    case 'ivr': {
      const order = '1234567890*#';
      const opts = ((b.options as Body[] | undefined) ?? [])
        .slice().sort((x, y) => order.indexOf(String(x.digit)) - order.indexOf(String(y.digit)));
      const keys = opts.map((o) => ({ slot: `option:${String(o.digit)}`, label: `${String(o.digit)}${o.label ? ` ${String(o.label)}` : ''}` }));
      const inv = dest(b.invalid_destination);
      const apart = inv !== null && JSON.stringify(inv) !== JSON.stringify(dest(b.timeout_destination));
      return apart
        ? [...keys, { slot: 'timeout_destination', label: 'No key' }, { slot: 'invalid_destination', label: 'Wrong key' }]
        : [...keys, { slot: 'timeout_destination', label: 'No key or wrong key' }];
    }
    case 'ring_group': return [{ slot: 'timeout_destination', label: 'No answer' }];
    case 'queue': {
      const mins = Math.round(Number(b.max_wait_sec ?? 600) / 60);
      const out: Exit[] = [{ slot: 'timeout_destination', label: `Waits ${mins} min` }];
      if (b.exit_key) out.push({ slot: 'exit_destination', label: `Presses ${String(b.exit_key)}` });
      return out;
    }
    case 'message': return [{ slot: 'next_destination', label: null }];
    case 'caller': return [{ slot: 'match_destination', label: 'On the list' }, { slot: 'no_match_destination', label: 'Anyone else' }];
    case 'lookup': return [
      ...((b.cases as Body[] | undefined) ?? []).map((c, i) => ({ slot: `case:${i}`, label: `“${String(c.value ?? '')}”` })),
      { slot: 'no_match_destination', label: 'Any other answer' },
      { slot: 'error_destination', label: 'No answer' },
    ];
  }
}

/** The exit a step carries on through: where "+" before its successor points, and what removing it rejoins. */
export const CONTINUE: Record<FlowPieceKind, string> = {
  inbound_route: 'destination', hours: 'open_destination', ivr: 'timeout_destination',
  ring_group: 'timeout_destination', queue: 'timeout_destination', message: 'next_destination',
  caller: 'no_match_destination', lookup: 'no_match_destination',
};

export function getSlot(b: Body, slot: string): Destination | null {
  if (slot.startsWith('option:')) {
    const digit = slot.slice(7);
    return dest(((b.options as Body[] | undefined) ?? []).find((o) => String(o.digit) === digit)?.destination);
  }
  if (slot.startsWith('case:')) return dest(((b.cases as Body[] | undefined) ?? [])[Number(slot.slice(5))]?.destination);
  return dest(b[slot]);
}

export function setSlot(b: Body, slot: string, d: Destination | null): Body {
  if (slot.startsWith('option:')) {
    const digit = slot.slice(7);
    const options = ((b.options as Body[] | undefined) ?? []).flatMap((o) => (
      String(o.digit) !== digit ? [o] : d ? [{ ...o, destination: d }] : []));
    return { ...b, options };
  }
  if (slot.startsWith('case:')) {
    const i = Number(slot.slice(5));
    return { ...b, cases: ((b.cases as Body[] | undefined) ?? []).map((c, j) => (j === i ? { ...c, destination: d } : c)) };
  }
  const next = { ...b, [slot]: d };
  // A queue's key to leave goes with where it leads: one without the other is switched off.
  if (slot === 'exit_destination' && !d) next.exit_key = null;
  return next;
}

// ------------------------------------------------------------------- new pieces
/** A name not yet taken by a piece of that kind: "New message", "New message 2". */
export function freshName(kind: FlowPieceKind, base: string, live: Live, changes: Changes): string {
  const taken = new Set(piecesOf(kind, live, changes).map((x) => x.name.toLowerCase()));
  for (let n = 1; ; n++) {
    const name = n === 1 ? base : `${base} ${n}`;
    if (!taken.has(name.toLowerCase())) return name;
  }
}

/** A new step, carrying on to `then` -- where the line it was put on used to lead. */
export function newBody(kind: FlowPieceKind, then: Destination | null, live: Live, changes: Changes): Body {
  switch (kind) {
    case 'message':
      return { name: freshName(kind, 'New message', live, changes), media_id: null, next_destination: then };
    case 'hours':
      return { name: freshName(kind, 'New hours check', live, changes), hours: { mode: 'schedule', schedule_id: live.businessId ?? '' },
        open_destination: then, closed_destination: null };
    case 'ivr':
      return { name: freshName(kind, 'New menu', live, changes), extension: null, greeting_media_id: null, invalid_media_id: null,
        timeout_sec: 5, digit_timeout_sec: 3, max_attempts: 3, answer_after_rings: 0, direct_dial: false,
        timeout_destination: then, invalid_destination: null, options: [] };
    case 'ring_group':
      return { name: freshName(kind, 'New hunt list', live, changes), extension: null, strategy: 'simultaneous', ring_time_sec: 20,
        skip_busy: true, timeout_destination: then, members: [], steps: [{ strategy: 'simultaneous', ring_sec: 20, prompt_media_id: null, members: [] }],
        outside_extension_id: null };
    case 'queue':
      return { name: freshName(kind, 'New queue', live, changes), extension: null, strategy: 'longest_idle', announce_position: false,
        announce_interval_sec: 60, periodic_media_id: null, periodic_interval_sec: 60, exit_key: null, exit_destination: null,
        max_wait_sec: 600, max_waiting_calls: 50, wrapup_sec: 10, require_disposition: false, workspace_controls: {},
        sticky_agent_days: null, sticky_ring_sec: 15, timeout_destination: then, moh_media_id: null, agents: [] };
    case 'inbound_route':
      return { name: freshName(kind, 'New call flow', live, changes), number_ids: [], destination: null, hours: { mode: 'always' },
        closed_destination: null, cid_name_prefix: null, is_enabled: true };
    case 'caller':
      return { name: freshName(kind, 'New caller check', live, changes), numbers: [], match_withheld: false,
        match_destination: null, no_match_destination: then };
    case 'lookup':
      return { name: freshName(kind, 'New look up', live, changes), url: '', method: 'GET', header_name: null, field: '',
        cases: [], no_match_destination: then, error_destination: then };
  }
}

// ------------------------------------------------------------------- the tree that is drawn
export type TermKind = 'extension' | 'voicemail' | 'external' | 'hangup' | 'goto' | 'flow' | 'unset' | 'missing';

export interface FlowNode {
  key: string;
  /** A step: a piece of the routing. Absent on an end. */
  piece?: { kind: FlowPieceKind; id: string };
  term?: TermKind;
  /** For a "Goes to": the step it goes to. */
  goto?: { kind: FlowPieceKind; id: string };
  name: string;
  detail: string[];
  /** "Open now" / "Closed now" under an hours check. */
  now?: boolean | null;
  mono?: boolean;
  badges: Array<'new' | 'changed' | 'needs-recording' | 'needs-people'>;
  shared?: string;
  children: Array<{ label: string | null; slot: string; node: FlowNode }>;
  parent?: { node: FlowNode; slot: string };
}

const STRATEGY: Record<string, string> = { simultaneous: 'all at once', sequential: 'in order', random: 'in random order' };
const QUEUE_STRATEGY: Record<string, string> = {
  longest_idle: 'The agent free longest', round_robin: 'Each agent in turn', ring_all: 'Every free agent at once',
  top_down: 'Agents in list order', agent_with_least_talk_time: 'Least talk time first', random: 'A random free agent',
};
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The flow from a route, as a tree: each piece drawn once, where it is first reached; met again, it
 * is a "Goes to" (docs/41 §4). `others` names, for each piece, the other routes that reach it.
 */
export function buildTree(
  routeId: string, live: Live, changes: Changes, others: ReadonlyMap<string, string[]>,
): FlowNode | null {
  const seen = new Set<string>();
  const route = pieceOf('inbound_route', routeId, live, changes);
  if (!route) return null;

  const badgesOf = (kind: FlowPieceKind, id: string): FlowNode['badges'] => {
    const c = changes.get(changeKey(kind, id));
    return c ? [c.op === 'create' ? 'new' : 'changed'] : [];
  };

  const term = (key: string, kind: TermKind, name: string, goto?: FlowNode['goto']): FlowNode =>
    ({ key, term: kind, name, detail: [], badges: [], children: [], ...(goto ? { goto } : {}) });

  const toNode = (key: string, d: Destination | null, isRoute: boolean): FlowNode => {
    if (!d) return isRoute ? term(key, 'unset', 'Not set yet') : term(key, 'hangup', 'Hang up');
    switch (d.type) {
      case 'hangup': return term(key, 'hangup', 'Hang up');
      case 'external': return term(key, 'external', d.number);
      case 'extension': {
        const e = live.extensions.find((x) => x.id === d.id);
        return e ? term(key, 'extension', `${e.number} ${e.display_name}`) : term(key, 'missing', 'An extension that no longer exists');
      }
      case 'voicemail': {
        const b = live.boxes.find((x) => x.id === d.id);
        return b ? term(key, 'voicemail', `${b.extension_number} ${b.extension_name}`) : term(key, 'missing', 'A mailbox that no longer exists');
      }
      // Another number's flow (docs/41 §16): an end of this canvas, opened on its own.
      case 'route': {
        const r = pieceOf('inbound_route', d.id, live, changes);
        return r ? term(key, 'flow', r.name) : term(key, 'missing', 'A call flow that no longer exists');
      }
      default: {
        const kind = KIND_OF_DEST[d.type];
        if (!kind) return term(key, 'missing', 'Unknown');
        const r = pieceOf(kind, d.id, live, changes);
        if (!r) return term(key, 'missing', `A ${KIND_LABEL[kind].toLowerCase()} that no longer exists`);
        if (seen.has(d.id)) return term(key, 'goto', r.name, { kind, id: d.id });
        return step(key, kind, d.id, r);
      }
    }
  };

  const step = (key: string, kind: FlowPieceKind, id: string, r: AnyRead): FlowNode => {
    seen.add(id);
    const b = bodyOf(kind, id, live, changes) ?? {};
    const node: FlowNode = { key, piece: { kind, id }, name: r.name, detail: [], badges: badgesOf(kind, id), children: [] };
    const elsewhere = (others.get(id) ?? []).filter((n) => n !== route.name);
    if (elsewhere.length > 0) node.shared = elsewhere.join(', ');
    switch (kind) {
      case 'inbound_route': {
        const x = r as InboundRoute;
        node.mono = true;
        node.detail = x.numbers.length === 0 ? ['No numbers yet']
          : x.numbers.length <= 2 ? x.numbers.map((n) => n.e164) : [x.numbers[0]!.e164, `and ${x.numbers.length - 1} more`];
        if (x.numbers.length === 0) node.mono = false;
        break;
      }
      case 'hours': {
        const x = r as HoursCheck;
        node.detail = [x.hours.mode === 'own' ? 'Its own hours'
          : x.hours.schedule_id && x.hours.schedule_id === live.businessId ? 'Business hours'
            : x.hours.schedule_name ?? 'No schedule chosen'];
        node.now = x.hours.open_now;
        break;
      }
      case 'ivr': {
        const x = r as IvrMenu & { greeting_media_name?: string | null };
        node.detail = [x.greeting_media_id ? `“${x.greeting_media_name ?? live.media.find((m) => m.id === x.greeting_media_id)?.name ?? 'Greeting'}”` : 'No greeting',
          `Plays up to ${plural(x.max_attempts, 'time', 'times')}`];
        break;
      }
      case 'ring_group': {
        const x = r as RingGroup;
        const n = x.members.length;
        node.detail = [`${plural(n, 'person', 'people')} · ${STRATEGY[x.strategy] ?? x.strategy}`,
          x.steps.length > 1 ? `${x.steps.length} steps` : `Rings for ${x.ring_time_sec} s`];
        if (n === 0) node.badges.push('needs-people');
        break;
      }
      case 'queue': {
        const x = r as Queue;
        node.detail = [plural(x.agents.length, 'agent', 'agents'), QUEUE_STRATEGY[x.strategy] ?? x.strategy];
        if (x.agents.length === 0) node.badges.push('needs-people');
        break;
      }
      case 'message': {
        const x = r as FlowMessage;
        const m = live.media.find((y) => y.id === x.media_id);
        node.detail = m ? [m.name, ...(m.duration_sec ? [`${m.duration_sec.toFixed(1)} s`] : [])] : ['No recording chosen'];
        if (!x.media_id) node.badges.push('needs-recording');
        break;
      }
      case 'caller': {
        const x = r as CallerCheck;
        const n = x.numbers.length;
        node.detail = [n === 0 ? 'No numbers yet' : n <= 2 ? x.numbers.join(', ') : `${x.numbers[0]} and ${n - 1} more`,
          ...(x.match_withheld ? ['and hidden numbers'] : [])];
        break;
      }
      case 'lookup': {
        const x = r as Lookup;
        let host = '';
        try { host = x.url ? new URL(x.url).hostname : ''; } catch { host = ''; }
        node.detail = [host || 'No address yet', x.field ? `Reads “${x.field}”` : 'No field chosen'];
        break;
      }
    }
    for (const ex of exitsOf(kind, b)) {
      const child = toNode(`${key}/${ex.slot}`, getSlot(b, ex.slot), kind === 'inbound_route');
      child.parent = { node, slot: ex.slot };
      node.children.push({ label: ex.label, slot: ex.slot, node: child });
    }
    return node;
  };

  return step(`r:${routeId}`, 'inbound_route', routeId, route);
}

/** For every piece, the routes that reach it, live: what "Also in…" says. */
export function reachByRoute(live: Live): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const index: Record<string, Map<string, AnyRead>> = {
    ivr: new Map(live.ivrs.map((x) => [x.id, x])), ring_group: new Map(live.groups.map((x) => [x.id, x])),
    queue: new Map(live.queues.map((x) => [x.id, x])), message: new Map(live.messages.map((x) => [x.id, x])),
    hours: new Map(live.hours.map((x) => [x.id, x])),
    caller: new Map(live.callers.map((x) => [x.id, x])), lookup: new Map(live.lookups.map((x) => [x.id, x])),
  };
  for (const route of live.routes) {
    const seen = new Set<string>();
    const walk = (d: Destination | null) => {
      if (!d || !('id' in d)) return;
      const kind = KIND_OF_DEST[d.type];
      if (!kind || seen.has(d.id)) return;
      const r = index[d.type]?.get(d.id);
      if (!r) return;
      seen.add(d.id);
      const b = toBody(kind, r);
      for (const ex of exitsOf(kind, b)) walk(getSlot(b, ex.slot));
    };
    walk(route.destination);
    walk(route.closed_destination);
    for (const id of seen) out.set(id, [...(out.get(id) ?? []), route.name]);
  }
  return out;
}
